// Admin tool: scan all jobs for data corruption and show results in a
// table. One button: "Run Audit". One result table. Read-only — never
// modifies anything in the database.
//
// Use this when team members report "job is on the calendar but I can't
// see it in Time Tracker" — you'll get a list of the corrupted jobs
// with plain-English explanations and suggested fixes.

import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ShieldAlert, RefreshCw, CheckCircle2, AlertTriangle, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SEVERITY_STYLES = {
  high: { bg: 'bg-red-50', border: 'border-red-300', badge: 'bg-red-600 hover:bg-red-700', icon: 'text-red-600' },
  medium: { bg: 'bg-amber-50', border: 'border-amber-300', badge: 'bg-amber-600 hover:bg-amber-700', icon: 'text-amber-600' },
  low: { bg: 'bg-slate-50', border: 'border-slate-300', badge: 'bg-slate-600 hover:bg-slate-700', icon: 'text-slate-600' },
};

export default function JobAudit() {
  const [loading, setLoading] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [report, setReport] = useState(null);

  const runAudit = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('fieldflow_token');
      const r = await axios.get(`${API}/admin/jobs/audit`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReport(r.data);
      if (r.data.issue_count === 0) {
        toast.success('No issues found — your job data is clean.');
      } else {
        toast.warning(`Found ${r.data.issue_count} issue(s) — see details below.`);
      }
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      toast.error(`Audit failed: ${detail}`, { duration: 10000 });
    } finally {
      setLoading(false);
    }
  };

  const fixCountDrift = async () => {
    // One-click safe fix: resync `daily_series_total` on all series.
    // Confirm because it modifies records — even though it's a single
    // derived field, the user should see the count before they commit.
    if (!window.confirm(
      'This will recalculate "Day X of Y" totals on every multi-day job so they match reality. ' +
      'It only updates that one number — no dates, teams, or files are changed. Continue?'
    )) return;
    setFixing(true);
    try {
      const token = localStorage.getItem('fieldflow_token');
      const r = await axios.post(`${API}/admin/jobs/audit/fix-count-drift`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(r.data.message || 'Fix applied.');
      // Re-run audit so the user sees the S3 count go to zero
      await runAudit();
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      toast.error(`Fix failed: ${detail}`, { duration: 10000 });
    } finally {
      setFixing(false);
    }
  };

  const openJob = (jobId) => {
    // Open the job's edit page in a new tab so the admin can fix it
    // without losing the audit results.
    window.open(`/jobs/${jobId}`, '_blank', 'noopener');
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-3">
            <ShieldAlert className="w-6 h-6 text-amber-600" />
            Job Data Audit
          </CardTitle>
          <p className="text-sm text-slate-600 mt-2">
            Scans every job in the database for data-integrity problems —
            mismatched dates, stale day-chips after reschedules, duplicate or
            missing days in a series. <strong>Read-only.</strong> Nothing is
            changed in your data. Use this when team members report a job
            shows on the calendar but not in Time Tracker.
          </p>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={runAudit}
              disabled={loading || fixing}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="run-audit-btn"
            >
              {loading ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Scanning…</>
              ) : (
                <><RefreshCw className="w-4 h-4 mr-2" /> Run Audit Now</>
              )}
            </Button>
            <Button
              onClick={fixCountDrift}
              disabled={loading || fixing}
              variant="outline"
              className="border-amber-600 text-amber-700 hover:bg-amber-50"
              data-testid="fix-count-drift-btn"
              title="Resyncs the stored Day X of Y total on every multi-day job to match reality. Safe — touches only that one number."
            >
              {fixing ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Fixing…</>
              ) : (
                <>Fix Count Drift (S3)</>
              )}
            </Button>
          </div>

          {report && (
            <div className="mt-6 space-y-4">
              {/* Summary */}
              <div className={`p-4 rounded-lg border-2 ${
                report.issue_count === 0
                  ? 'bg-green-50 border-green-300'
                  : 'bg-amber-50 border-amber-300'
              }`}>
                <div className="flex items-center gap-3">
                  {report.issue_count === 0 ? (
                    <CheckCircle2 className="w-6 h-6 text-green-600" />
                  ) : (
                    <AlertTriangle className="w-6 h-6 text-amber-600" />
                  )}
                  <div>
                    <div className="font-semibold text-lg">
                      Scanned {report.scanned} jobs ·{' '}
                      {report.issue_count === 0
                        ? 'No issues found ✓'
                        : `${report.issue_count} issue(s) found`}
                    </div>
                    {report.issue_count > 0 && (
                      <div className="text-sm text-slate-700 mt-1">
                        {Object.entries(report.by_code).map(([code, count]) => (
                          <span key={code} className="mr-3">
                            <strong>{code}:</strong> {count}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Legend */}
              {report.issue_count > 0 && (
                <div className="text-xs text-slate-600 bg-slate-50 rounded p-3 border border-slate-200">
                  <strong>Codes:</strong>{' '}
                  {Object.entries(report.legend).map(([k, v]) => (
                    <span key={k} className="block">
                      <span className="font-mono font-semibold">{k}</span> — {v}
                    </span>
                  ))}
                </div>
              )}

              {/* Issue cards */}
              {report.issues.map((issue, idx) => {
                const sev = SEVERITY_STYLES[issue.severity] || SEVERITY_STYLES.low;
                return (
                  <div
                    key={`${issue.job_id}-${idx}`}
                    className={`p-4 rounded-lg border-2 ${sev.bg} ${sev.border}`}
                    data-testid={`audit-issue-${idx}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <Badge className={`${sev.badge} text-white`}>
                            {issue.code} · {issue.severity.toUpperCase()}
                          </Badge>
                          <span className="font-semibold text-slate-900">
                            {issue.label}
                          </span>
                        </div>
                        <div className="font-mono text-sm font-semibold text-amber-700 mb-1">
                          {issue.job_number || '(no job number)'}
                          {issue.job_title && (
                            <span className="font-sans font-normal text-slate-700 ml-2">
                              — {issue.job_title}
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-slate-700 mt-2">
                          <strong>What's wrong:</strong> {issue.explanation}
                        </div>
                        <div className="text-sm text-slate-700 mt-1">
                          <strong>How to fix:</strong> {issue.suggestion}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openJob(issue.job_id)}
                        className="flex-shrink-0"
                        data-testid={`open-job-${idx}`}
                      >
                        <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                        Open Job
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
